'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Radice del select: gestisce apertura/chiusura e valore selezionato. */
const Select = SelectPrimitive.Root;

/** Raggruppa logicamente più {@link SelectItem} sotto una {@link SelectLabel}. */
const SelectGroup = SelectPrimitive.Group;

/** Mostra il valore selezionato (o il placeholder) dentro {@link SelectTrigger}. */
const SelectValue = SelectPrimitive.Value;

/** Props di {@link SelectTrigger}: nessuna prop custom. */
export interface SelectTriggerProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Trigger
> {}

/** Bottone che apre il menu del select, mostra il valore corrente. */
const SelectTrigger = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Trigger>,
	SelectTriggerProps
>(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Trigger
		ref={ref}
		className={cn(
			'flex h-9 w-full items-center justify-between rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-xs text-foreground ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:border-accent focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 transition-colors',
			className,
		)}
		{...props}
	>
		{children}
		<SelectPrimitive.Icon asChild>
			<ChevronDown className="h-4 w-4 opacity-50" />
		</SelectPrimitive.Icon>
	</SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

/** Props di {@link SelectScrollUpButton}: nessuna prop custom. */
export interface SelectScrollUpButtonProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.ScrollUpButton
> {}

/** Freccia "scorri su" mostrata quando la lista opzioni non entra nello spazio disponibile. */
const SelectScrollUpButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
	SelectScrollUpButtonProps
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollUpButton
		ref={ref}
		className={cn('flex cursor-default items-center justify-center py-1', className)}
		{...props}
	>
		<ChevronUp className="h-4 w-4" />
	</SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

/** Props di {@link SelectScrollDownButton}: nessuna prop custom. */
export interface SelectScrollDownButtonProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.ScrollDownButton
> {}

/** Freccia "scorri giù" mostrata quando la lista opzioni non entra nello spazio disponibile. */
const SelectScrollDownButton = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
	SelectScrollDownButtonProps
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollDownButton
		ref={ref}
		className={cn('flex cursor-default items-center justify-center py-1', className)}
		{...props}
	>
		<ChevronDown className="h-4 w-4" />
	</SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

/** Props di {@link SelectContent}: eredita gli attributi standard di Radix (`position`, ecc.). */
export interface SelectContentProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Content
> {}

/** Pannello con l'elenco delle opzioni, renderizzato in un portale. */
const SelectContent = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Content>,
	SelectContentProps
>(({ className, children, position = 'popper', ...props }, ref) => (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
			ref={ref}
			className={cn(
				'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
				position === 'popper' &&
					'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
				className,
			)}
			position={position}
			{...props}
		>
			<SelectScrollUpButton />
			<SelectPrimitive.Viewport
				className={cn(
					'p-1',
					position === 'popper' &&
						'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
				)}
			>
				{children}
			</SelectPrimitive.Viewport>
			<SelectScrollDownButton />
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

/** Props di {@link SelectLabel}: nessuna prop custom. */
export interface SelectLabelProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Label
> {}

/** Etichetta non interattiva per un {@link SelectGroup} di opzioni. */
const SelectLabel = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Label>,
	SelectLabelProps
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
		{...props}
	/>
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

/** Props di {@link SelectItem}: eredita gli attributi standard di Radix (`value`, `disabled`, ecc.). */
export interface SelectItemProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Item
> {}

/** Singola opzione selezionabile del menu. */
const SelectItem = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
	({ className, children, ...props }, ref) => (
		<SelectPrimitive.Item
			ref={ref}
			className={cn(
				'relative flex w-full cursor-default select-none items-center rounded-md py-1.5 pl-8 pr-2 text-xs outline-none focus:bg-accent/10 focus:text-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
				className,
			)}
			{...props}
		>
			<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<Check className="h-4 w-4" />
				</SelectPrimitive.ItemIndicator>
			</span>

			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	),
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

/** Props di {@link SelectSeparator}: nessuna prop custom. */
export interface SelectSeparatorProps extends React.ComponentPropsWithoutRef<
	typeof SelectPrimitive.Separator
> {}

/** Linea divisoria sottile tra gruppi di opzioni. */
const SelectSeparator = React.forwardRef<
	React.ElementRef<typeof SelectPrimitive.Separator>,
	SelectSeparatorProps
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={cn('-mx-1 my-1 h-px bg-muted', className)}
		{...props}
	/>
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
	Select,
	SelectGroup,
	SelectValue,
	SelectTrigger,
	SelectContent,
	SelectLabel,
	SelectItem,
	SelectSeparator,
	SelectScrollUpButton,
	SelectScrollDownButton,
};
